export interface EnergyReading {
	id: string;
	tenantId: string;
	meterId: string;
	timestamp: Date;
	energiaImmessa: number;
	energiaPrelevata: number;
	energiaReattiva?: number;
	source: 'dso' | 'inverter' | 'manual' | 'gateway';
}

export interface SharedEnergy {
	hour: Date;
	totalImmessa: number;
	totalPrelevata: number;
	energiaCondivisa: number;
}

export interface MonthlyEnergyData {
	month: number;
	production: number;
	consumption: number;
	shared: number;
	surplus: number;
}

export interface DashboardMemberKpi {
	memberId: string;
	productionTodayKwh: number;
	consumptionTodayKwh: number;
	sharedMonthKwh: number;
	/** Produzione e consumo del socio nel mese, dai suoi misuratori (INB-363). */
	productionMonthKwh?: number;
	consumptionMonthKwh?: number;
}

export interface DashboardHourlyKpi {
	hour: string;
	productionKwh: number;
	consumptionKwh: number;
	sharedKwh: number;
}

/**
 * Totali energetici di una finestra temporale.
 *
 * `incentivizedKwh` è la quota di energia condivisa che sta sotto la soglia GSE:
 * la parte prodotta da impianti preesistenti non è incentivabile, e la dashboard
 * mostra i due numeri affiancati proprio perché la differenza non è ovvia.
 */
export interface DashboardEnergyTotals {
	productionKwh: number;
	consumptionKwh: number;
	sharedKwh: number;
	incentivizedKwh: number;
	selfConsumptionPct: number | null;
	/** Energia condivisa oltre la soglia di incentivabilità. */
	eccedenzaKwh: number;
}

/**
 * Quota stimata (0-1) di un valore energetico: la parte che viene dai livelli
 * stimato e tipico della curva, non da una misura né da un dato GSE
 * (INB-354). Finisce nella spiegazione della «i».
 */
export interface EnergyStimaShare {
	produzione: number;
	consumo: number;
}

/** Sopra questa quota stimata la «i» del valore dice che è in parte stimato. */
export const STIMA_BADGE_MIN = 0.01;

/** Il valore va segnato come stimato? */
export function isStimato(share: number | null | undefined): boolean {
	return (share ?? 0) >= STIMA_BADGE_MIN;
}

/** «38% del valore è stimato…»: la spiegazione dietro la «i». */
export function stimaTooltip(share: number): string {
	return `${Math.round(share * 100)}% del valore è stimato: per quei quarti d'ora manca ancora la misura del contatore o il dato del GSE.`;
}

/**
 * La riga sulla stima da aggiungere alla spiegazione della «i», o niente se il
 * valore è misurato. Non c'è più un'etichetta «stima» sulla card: diceva poco
 * e affollava il titolo; la quota sta dove si legge il resto del calcolo.
 */
export function stimaNote(share: number | null | undefined): string | undefined {
	return isStimato(share) ? stimaTooltip(share as number) : undefined;
}

export interface DashboardKpiSummary {
	asOf: string;
	today: DashboardHourlyKpi[];
	curve: DashboardHourlyKpi[];
	/**
	 * Totali della finestra scelta dall'operatore (`dayFrom`…`to`). Su «mese in
	 * corso» e «ultima validazione» coincide con `month` — è il caso «oggi» a
	 * separarli, perché lì convivono il KPI del giorno e il maturato del mese.
	 */
	period: DashboardEnergyTotals;
	month: DashboardEnergyTotals;
	members: DashboardMemberKpi[];
	/**
	 * Ultima lettura misurata dal contatore (ISO), `null` se non ne è mai
	 * arrivata una: decide il badge «LIVE» con {@link isFeedLive} (INB-333).
	 * Assente dove il riepilogo non passa dall'API.
	 */
	ultimaLetturaAt?: string | null;
	/**
	 * Quanta parte dei valori è stimata, per finestra e per socio (mese):
	 * il badge «stima» (INB-354). Assente dove il riepilogo non passa dall'API.
	 */
	stima?: {
		period: EnergyStimaShare;
		month: EnergyStimaShare;
		members: Record<string, EnergyStimaShare>;
	};
}

/** Oltre quanti minuti dall'ultima lettura il feed non è più «LIVE». */
export const FEED_LIVE_MAX_MINUTI = 60;

/**
 * Il feed del contatore è vivo? Solo con una lettura nell'ultima ora: il
 * contatore misura al quarto d'ora, e un'ora di silenzio è già un feed fermo.
 */
export function isFeedLive(ultimaLetturaAt: string | null | undefined, now = new Date()): boolean {
	if (!ultimaLetturaAt) return false;
	const t = new Date(ultimaLetturaAt).getTime();
	return Number.isFinite(t) && now.getTime() - t <= FEED_LIVE_MAX_MINUTI * 60_000;
}
